import { useEffect } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { pwaUpdateStore } from "./pwaUpdateStore";
import { usePwaUpdate } from "./usePwaUpdate";

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
const UPDATE_TOAST_ID = "pwa-update";

// Set only in the tab where the update was applied: the new worker takes over every tab at once.
let reloadWhenActivated = false;
let swRegistration: ServiceWorkerRegistration | undefined;
// Until the person touches the page, reloading it costs nothing, so a pending version is applied on its own.
let interacted = false;

if (typeof window !== "undefined") {
  // A chunk of the version this tab runs is gone (a newer deploy replaced it): reloading is the way forward.
  window.addEventListener("vite:preloadError", () => pwaUpdateStore.markActivatedElsewhere());
  const markInteracted = () => {
    interacted = true;
  };
  window.addEventListener("pointerdown", markInteracted, { capture: true, once: true });
  window.addEventListener("keydown", markInteracted, { capture: true, once: true });
}

export function PwaUpdatePrompt() {
  const { t } = useTranslations("global");
  const update = usePwaUpdate();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onNeedReload() {
      if (reloadWhenActivated) window.location.reload();
      else pwaUpdateStore.markActivatedElsewhere();
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      swRegistration = registration;
      window.setInterval(() => {
        registration.update().catch(() => undefined);
      }, UPDATE_CHECK_INTERVAL_MS);
    },
  });

  useEffect(() => {
    if (!needRefresh) return;
    pwaUpdateStore.markAvailable(() => {
      reloadWhenActivated = true;
      const waiting = swRegistration?.waiting;
      // A page no worker controls yet (the first visit) never sees the takeover: it reloads once it is active.
      if (waiting && !navigator.serviceWorker.controller) {
        waiting.addEventListener("statechange", () => {
          if (waiting.state === "activated") window.location.reload();
        });
      }
      void updateServiceWorker(true);
    });
    // Mid-session it waits for the user menu or the next time the app opens: a reload could drop a payment.
    if (!interacted) pwaUpdateStore.apply();
  }, [needRefresh, updateServiceWorker]);

  useEffect(() => {
    if (update.state !== "activated-elsewhere") return;
    colocaToast.info(t("pwa_update_activated_elsewhere"), {
      id: UPDATE_TOAST_ID,
      duration: Infinity,
      action: { label: t("pwa_reload_action"), onClick: update.apply },
    });
  }, [t, update.apply, update.state]);

  return null;
}
